import React, { useEffect, useState, useRef } from "react";
import { Post, formatDate, extractTableOfContents, TableOfContentsItem } from "../utils/posts";
import { useFootnoteNavigation } from "../utils/footnotes";
import { projects as allProjects } from "../generated/projects";
import ReadingProgress from "./ReadingProgress";
import Prism from "prismjs";
import "prismjs/components/prism-typescript";
import "prismjs/components/prism-javascript";
import "prismjs/components/prism-jsx";
import "prismjs/components/prism-tsx";
import "prismjs/components/prism-css";
import "prismjs/components/prism-bash";
import "prismjs/components/prism-rust";
import "prismjs/components/prism-json";

interface BlogPostProps {
  post: Post;
  onBack: () => void;
}

export default function BlogPost({ post, onBack }: BlogPostProps) {
  const [toc, setToc] = useState<TableOfContentsItem[]>([]);
  const [activeId, setActiveId] = useState<string>("");
  const contentRef = useRef<HTMLDivElement>(null);

  const readingTime = Math.max(
    1,
    Math.round(post.content.split(/\s+/).length / 200)
  );

  useEffect(() => {
    document.title = `${post.title} — Tadej Gungl`;
  }, [post.title]);

  useEffect(() => {
    if (contentRef.current) {
      const tocItems = extractTableOfContents(post.htmlContent);
      setToc(tocItems);

      const headings = contentRef.current.querySelectorAll("h2, h3");
      headings.forEach((heading) => {
        const text = heading.textContent || "";
        const id = text.toLowerCase().replace(/[^\w]+/g, "-");
        heading.id = id;
      });

      Prism.highlightAllUnder(contentRef.current);

      const scrollContainer = document.getElementById("scroll-container");
      const observer = new IntersectionObserver(
        (entries) => {
          const visibleEntries = entries.filter(entry => entry.isIntersecting);
          if (visibleEntries.length > 0) {
            const topEntry = visibleEntries.reduce((prev, current) => {
              return prev.boundingClientRect.top < current.boundingClientRect.top ? prev : current;
            });
            setActiveId(topEntry.target.id);
          }
        },
        {
          root: scrollContainer,
          rootMargin: "-120px 0px -70% 0px",
          threshold: [0, 0.5, 1],
        }
      );

      headings.forEach((heading) => observer.observe(heading));

      return () => {
        headings.forEach((heading) => observer.unobserve(heading));
      };
    }
  }, [post.htmlContent]);

  useFootnoteNavigation(contentRef, post.htmlContent);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const scrollContainer = document.getElementById("scroll-container");
      if (scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const targetScroll = scrollContainer.scrollTop + elementRect.top - containerRect.top - 100;
        scrollContainer.scrollTo({
          top: targetScroll,
          behavior: "smooth",
        });
      }
    }
  };

  return (
    <div className="min-h-screen w-full bg-Base text-Text pt-20 pb-16">
      <ReadingProgress />
      <div className="px-6 md:px-12 xl:grid xl:grid-cols-[13rem_minmax(0,46rem)_13rem] xl:justify-center xl:gap-12">
        {/* Table of Contents - Left Rail */}
        <aside className="hidden xl:block">
          {toc.length > 0 && (
            <div className="border-l hairline pl-4 mt-16">
              <h3 className="label-caps mb-4">Contents</h3>
              <nav className="space-y-2">
                {toc.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`
                      block w-full text-left text-sm font-mono transition-colors duration-200 cursor-pointer py-0.5
                      ${item.level === 3 ? "pl-4" : ""}
                      ${
                        activeId === item.id
                          ? "text-Sapphire"
                          : "text-Subtext0 hover:text-Text"
                      }
                    `}
                  >
                    {item.text}
                  </button>
                ))}
              </nav>
            </div>
          )}
        </aside>

        <div className="max-w-3xl mx-auto w-full min-w-0 xl:mx-0 xl:max-w-none">
          {/* Back Button */}
          <button
            onClick={onBack}
            className="mb-12 font-mono text-xs uppercase tracking-[0.15em] text-Subtext0 hover:text-Sapphire transition-colors cursor-pointer"
          >
            ← Back to Posts
          </button>

          {/* Article Content */}
          <article className="min-w-0">
            {/* Article Header */}
            <header className="mb-12 border-b hairline pb-10">
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-4">
                <span className="label-caps">
                  By Tadej Gungl · <time>{formatDate(post.date)}</time> · {readingTime} min read
                </span>
                {post.project && (() => {
                  const project = allProjects.find(p => p.slug === post.project);
                  return project ? (
                    <span className="label-caps text-Sapphire">
                      Part of: {project.title}
                    </span>
                  ) : null;
                })()}
                {post.tags.length > 0 && (
                  <span className="label-caps">{post.tags.join(" · ")}</span>
                )}
              </div>

              <h1 className="font-display font-medium text-3xl md:text-5xl text-Text mb-4 leading-tight">
                {post.title}
              </h1>

              <p className="text-Subtext0 font-sans text-base md:text-lg">
                {post.description}
              </p>
            </header>

            {/* Article Body */}
            <div
              ref={contentRef}
              className="prose-brutalist"
              dangerouslySetInnerHTML={{ __html: post.htmlContent }}
            />
          </article>
        </div>
      </div>
    </div>
  );
}
